import React, { useEffect, useRef, useState, ReactNode, CSSProperties } from 'react';

export const COLORS = {
  blue: '#1226AA',
  blueDeep: '#0a1b80',
  dark: '#252424',
  mid: '#6b7280',
  light: '#f3f4f6',
  border: '#e5e7eb',
  white: '#ffffff',
  pageBg: '#f7f8fa',
  blueSoft: '#eef0fb',
  red: '#ef4444',
  redSoft: '#fef2f2',
  green: '#16a34a',
  greenSoft: '#f0fdf4',
  purple: '#7c3aed',
  purpleSoft: '#f5f3ff',
  cyan: '#0891b2',
  cyanSoft: '#ecfeff',
  orange: '#f59e0b',
  orangeSoft: '#fff7ed',
  amber: '#f59e0b',
} as const;

export const GRADIENTS = {
  brand:    'linear-gradient(120deg, #1226AA 0%, #4f46e5 100%)',
  brandSoft:'linear-gradient(120deg, rgba(18,38,170,0.06) 0%, rgba(124,58,237,0.06) 100%)',
  ai:       'linear-gradient(120deg, #1226AA 0%, #4f46e5 50%, #7c3aed 100%)',
  aiSoft:   'linear-gradient(120deg, rgba(18,38,170,0.08) 0%, rgba(124,58,237,0.08) 100%)',
  warm:     'linear-gradient(120deg, #f59e0b 0%, #f97316 100%)',
  teal:     'linear-gradient(120deg, #0891b2 0%, #0ea5e9 100%)',
} as const;

export const SHADOWS = {
  sm:  '0 1px 2px rgba(15, 23, 42, 0.04)',
  md:  '0 4px 12px rgba(15, 23, 42, 0.06)',
  lg:  '0 10px 28px rgba(15, 23, 42, 0.08)',
  brand: '0 10px 28px rgba(18, 38, 170, 0.14)',
} as const;

// ─── Icons ──────────────────────────────────────────────────────────────────

export const Icons = {
  dashboard: (c = 'currentColor') => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  clients: (c = 'currentColor') => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  docs: (c = 'currentColor') => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  search: (c = '#9ca3af') => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  download: (c = 'currentColor') => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  share: (c = 'currentColor') => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  ),
  chevRight: (c = 'currentColor') => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  chevDown: (c = 'currentColor') => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  close: (c = 'currentColor') => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  check: (c = 'currentColor') => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  agent: (c = 'currentColor') => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  ),
  prompts: (c = 'currentColor') => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="13" x2="13" y2="13" />
    </svg>
  ),
  plus: (c = 'currentColor') => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  trash: (c = 'currentColor') => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  ),
  user: (c = 'currentColor') => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  logout: (c = 'currentColor') => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  file: (ext: string): JSX.Element => {
    const e = (ext || '').toLowerCase();
    const col =
      e === 'pdf' ? COLORS.red :
      e === 'xlsx' || e === 'xls' || e === 'csv' ? COLORS.green :
      e === 'docx' || e === 'doc' ? COLORS.blue :
      e === 'png' || e === 'jpg' || e === 'jpeg' || e === 'gif' || e === 'img' ? COLORS.orange :
      COLORS.mid;
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
      </svg>
    );
  },
  sparkle: (c = 'currentColor', size = 16) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.8 4.6L18 9l-4.2 1.4L12 15l-1.8-4.6L6 9l4.2-1.4L12 3z" />
      <path d="M19 14l.9 2.3L22 17l-2.1.7L19 20l-.9-2.3L16 17l2.1-.7L19 14z" />
    </svg>
  ),
  filter: (c = 'currentColor', size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  folder: (c = 'currentColor', size = 16) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  refresh: (c = 'currentColor', size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  grid: (c = 'currentColor', size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
  ),
  list: (c = 'currentColor', size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  building: (c = 'currentColor', size = 18) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="9" y1="6" x2="9" y2="6.01" /><line x1="15" y1="6" x2="15" y2="6.01" />
      <line x1="9" y1="10" x2="9" y2="10.01" /><line x1="15" y1="10" x2="15" y2="10.01" />
      <line x1="9" y1="14" x2="9" y2="14.01" /><line x1="15" y1="14" x2="15" y2="14.01" />
      <path d="M10 22v-4h4v4" />
    </svg>
  ),
  users: (c = 'currentColor', size = 18) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  inbox: (c = 'currentColor', size = 40) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  command: (c = 'currentColor', size = 12) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
    </svg>
  ),
};

// ─── Logo ───────────────────────────────────────────────────────────────────

export const SDLogoMark: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="8" fill={COLORS.blue} />
    <path d="M10 22c0-3 2-5 6-5s6-2 6-5" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
    <circle cx="10" cy="11" r="2.5" fill="#fff" />
    <circle cx="22" cy="21" r="2.5" fill="#fff" />
  </svg>
);

// ─── Avatar ─────────────────────────────────────────────────────────────────

export const Avatar: React.FC<{ initials: string; size?: number; bg?: string }> = ({ initials, size = 30, bg }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: bg || COLORS.blueSoft, color: bg ? '#fff' : COLORS.blue,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
  }}>{initials}</div>
);

// ─── Button ─────────────────────────────────────────────────────────────────

export const Btn: React.FC<{
  children: ReactNode;
  primary?: boolean;
  small?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  style?: CSSProperties;
  title?: string;
}> = ({ children, primary, small, onClick, disabled, style, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      padding: small ? '5px 12px' : '8px 18px',
      borderRadius: 8,
      border: primary ? 'none' : `1px solid ${COLORS.border}`,
      background: disabled ? COLORS.light : primary ? COLORS.blue : COLORS.white,
      color: disabled ? COLORS.mid : primary ? '#fff' : COLORS.dark,
      fontSize: small ? 12 : 13,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s',
      fontFamily: 'inherit',
      ...style,
    }}
  >
    {children}
  </button>
);

// ─── Type Badge ─────────────────────────────────────────────────────────────

const TYPE_BADGE_MAP: Record<string, { bg: string; color: string }> = {
  Individual:  { bg: COLORS.blueSoft,   color: COLORS.blue },
  Trust:       { bg: COLORS.purpleSoft, color: COLORS.purple },
  Company:     { bg: COLORS.greenSoft,  color: COLORS.green },
  SMSF:        { bg: COLORS.cyanSoft,   color: COLORS.cyan },
  Partnership: { bg: COLORS.orangeSoft, color: COLORS.orange },
  Joint:       { bg: COLORS.blueSoft,   color: COLORS.blue },
  Other:       { bg: COLORS.light,      color: COLORS.mid },
};

export const TypeBadge: React.FC<{ type: string | null | undefined }> = ({ type }) => {
  const key = type || 'Other';
  const s = TYPE_BADGE_MAP[key] || TYPE_BADGE_MAP.Other;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
    }}>{key}</span>
  );
};

// Group type badge: corporate_group | family_group → "Corporate" / "Family"
export const GroupTypeBadge: React.FC<{ type: 'corporate_group' | 'family_group' | string | null }> = ({ type }) => {
  const isCorporate = type === 'corporate_group';
  const label = isCorporate ? 'Corporate' : type === 'family_group' ? 'Family' : (type ?? 'Group');
  const s = isCorporate
    ? { bg: COLORS.purpleSoft, color: COLORS.purple }
    : { bg: COLORS.blueSoft, color: COLORS.blue };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
    }}>{label}</span>
  );
};

// Status badge for documents (text-driven)
export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { bg: string; color: string }> = {
    Draft:    { bg: '#fef9c3',         color: '#854d0e' },
    Review:   { bg: COLORS.blueSoft,   color: COLORS.blue },
    Approved: { bg: COLORS.greenSoft,  color: COLORS.green },
    Stored:       { bg: COLORS.light,      color: COLORS.mid },
    'Not indexed': { bg: COLORS.orangeSoft, color: COLORS.orange },
  };
  const s = map[status] || map.Stored;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, letterSpacing: 0.2,
    }}>{status}</span>
  );
};

// ─── Dropdown ───────────────────────────────────────────────────────────────

export interface DropdownOption<V = string | number> {
  label: string;
  value: V;
  sub?: string;
}

export function Dropdown<V extends string | number>(props: {
  label: string;
  options: DropdownOption<V>[];
  value: V | '' | null | undefined;
  onSelect: (value: V | '') => void;
  minWidth?: number;
  placeholder?: string;
}): JSX.Element {
  const { label, options, value, onSelect, minWidth = 180, placeholder } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sel = options.find((o) => o.value === value);
  const isEmpty = value === '' || value === null || value === undefined;
  const showClear = !!placeholder && !isEmpty;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
          background: COLORS.white, fontSize: 13, fontWeight: 500, color: COLORS.dark,
          cursor: 'pointer', minWidth, fontFamily: 'inherit',
        }}
      >
        <span style={{ color: COLORS.mid, marginRight: 4 }}>{label}:</span>
        {isEmpty && placeholder ? (
          <span style={{ color: '#c0c4cc', fontWeight: 400, fontStyle: 'italic' }}>{placeholder}</span>
        ) : (
          sel?.label ?? 'All'
        )}
        {showClear ? (
          <span
            onClick={(e) => { e.stopPropagation(); onSelect(''); }}
            style={{ display: 'flex', alignItems: 'center', marginLeft: 2, cursor: 'pointer' }}
            title="Clear filter"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={COLORS.mid} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        ) : Icons.chevDown(COLORS.mid)}
      </button>
      {open && (
        <div
          className="crm-anim-slideDown"
          style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4,
            background: COLORS.white, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            zIndex: 100, minWidth, overflow: 'hidden', maxHeight: 280, overflowY: 'auto',
          }}
        >
          {options.map((o) => (
            <div
              key={String(o.value)}
              onClick={() => { onSelect(o.value); setOpen(false); }}
              style={{
                padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                background: o.value === value ? COLORS.blueSoft : 'transparent',
                color: o.value === value ? COLORS.blue : COLORS.dark,
                fontWeight: o.value === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = COLORS.light; }}
              onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {o.label}
              {o.sub && <span style={{ display: 'block', fontSize: 11, color: COLORS.mid }}>{o.sub}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Table primitives ───────────────────────────────────────────────────────

export const TH: React.FC<{ children?: ReactNode; style?: CSSProperties }> = ({ children, style }) => (
  <th style={{
    padding: '10px 14px', textAlign: 'left',
    fontSize: 11, fontWeight: 600, color: COLORS.mid,
    textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap', ...style,
  }}>{children}</th>
);

export const TD: React.FC<{ children?: ReactNode; style?: CSSProperties; onClick?: React.MouseEventHandler<HTMLTableCellElement> }> = ({ children, style, onClick }) => (
  <td onClick={onClick} style={{
    padding: '12px 14px', fontSize: 13, color: COLORS.dark,
    borderBottom: `1px solid ${COLORS.border}`, ...style,
  }}>{children}</td>
);

// ─── Stat card ──────────────────────────────────────────────────────────────

export const StatCard: React.FC<{ label: string; value: ReactNode; sub?: string; accent?: string }> = ({ label, value, sub, accent }) => (
  <div style={{
    background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`,
    padding: '18px 22px', flex: 1, minWidth: 140,
  }}>
    <div style={{ fontSize: 12, color: COLORS.mid, fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: accent || COLORS.dark }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: COLORS.mid, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Toast ──────────────────────────────────────────────────────────────────

export interface ToastMessage { id: number; text: string; }

export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const show = (text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2400);
  };
  const node = (
    <div style={{ position: 'fixed', bottom: 20, right: 20, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 400 }}>
      {toasts.map((t) => (
        <div key={t.id} className="crm-anim-slideUp" style={{
          background: COLORS.dark, color: '#fff', padding: '10px 16px',
          borderRadius: 10, fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>{t.text}</div>
      ))}
    </div>
  );
  return { show, node };
}

// ─── Spinner / status messages ──────────────────────────────────────────────

export const Spinner: React.FC<{ label?: string }> = ({ label }) => (
  <div style={{ padding: 24, color: COLORS.mid, fontSize: 13, textAlign: 'center' }}>
    {label || 'Loading...'}
  </div>
);

export const ErrorBlock: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    background: COLORS.redSoft, border: `1px solid ${COLORS.red}33`, color: COLORS.red,
    borderRadius: 10, padding: '12px 16px', fontSize: 13,
  }}>{message}</div>
);

export const EmptyBlock: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div style={{ padding: 32, textAlign: 'center', color: COLORS.mid, fontSize: 13 }}>{children}</div>
);

// ─── Modal ──────────────────────────────────────────────────────────────────

export const Modal: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  children: ReactNode;
  footer?: ReactNode;
  disableBackdropClose?: boolean;
}> = ({ open, title, onClose, width = 480, children, footer, disableBackdropClose = false }) => {
  // ESC closes (unless backdrop close is disabled)
  useEffect(() => {
    if (!open || disableBackdropClose) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose, disableBackdropClose]);

  if (!open) return null;
  return (
    <div
      onClick={disableBackdropClose ? undefined : onClose}
      className="crm-anim-fadeIn"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 300, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="crm-anim-slideUp"
        style={{
          background: COLORS.white, borderRadius: 16, width: '100%', maxWidth: width,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{
          padding: '18px 24px 14px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 0 }}
            aria-label="Close"
          >
            {Icons.close(COLORS.mid)}
          </button>
        </div>
        <div style={{ padding: '18px 24px', overflowY: 'auto' }}>
          {children}
        </div>
        {footer && (
          <div style={{
            padding: '14px 24px', borderTop: `1px solid ${COLORS.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: 8, background: COLORS.pageBg,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── FormField (label + control wrapper) ────────────────────────────────────

export const FormField: React.FC<{
  label: string;
  required?: boolean;
  error?: string | null;
  hint?: string;
  children: ReactNode;
}> = ({ label, required, error, hint, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{
      display: 'block', fontSize: 12, fontWeight: 600,
      color: COLORS.mid, marginBottom: 4,
    }}>
      {label}{required && <span style={{ color: COLORS.red, marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {hint && !error && <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 4 }}>{hint}</div>}
    {error && <div style={{ fontSize: 11, color: COLORS.red, marginTop: 4 }}>{error}</div>}
  </div>
);

export const TextInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}> = ({ value, onChange, placeholder, type = 'text', disabled, autoFocus }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    autoFocus={autoFocus}
    style={{
      width: '100%', padding: '8px 12px', borderRadius: 8,
      border: `1px solid ${COLORS.border}`, fontSize: 13,
      outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
      background: disabled ? COLORS.light : COLORS.white,
      color: disabled ? COLORS.mid : COLORS.dark,
    }}
  />
);

export const Select: React.FC<{
  value: string | number | '';
  onChange: (v: string) => void;
  options: Array<{ label: string; value: string | number }>;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder, disabled }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    style={{
      width: '100%', padding: '8px 12px', borderRadius: 8,
      border: `1px solid ${COLORS.border}`, fontSize: 13,
      outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
      background: disabled ? COLORS.light : COLORS.white,
      color: disabled ? COLORS.mid : COLORS.dark,
    }}
  >
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={String(o.value)} value={o.value}>{o.label}</option>
    ))}
  </select>
);

// ─── Tabs (used by CreateEntityModal) ───────────────────────────────────────

export function Tabs<V extends string>(props: {
  value: V;
  onChange: (v: V) => void;
  options: Array<{ label: string; value: V }>;
}): JSX.Element {
  return (
    <div style={{
      display: 'flex', gap: 4, padding: 4, borderRadius: 10,
      background: COLORS.light, marginBottom: 16,
    }}>
      {props.options.map((o) => {
        const active = o.value === props.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => props.onChange(o.value)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 6, border: 'none',
              background: active ? COLORS.white : 'transparent',
              color: active ? COLORS.blue : COLORS.mid,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Page Header (gradient strip with title, subtitle, breadcrumb, actions) ─

export const PageHeader: React.FC<{
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  accent?: 'brand' | 'ai' | 'warm' | 'teal';
}> = ({ title, subtitle, breadcrumb, actions, icon, accent = 'brand' }) => {
  const gradient =
    accent === 'ai'   ? GRADIENTS.aiSoft   :
    accent === 'warm' ? 'linear-gradient(120deg, rgba(245,158,11,0.10) 0%, rgba(249,115,22,0.06) 100%)' :
    accent === 'teal' ? 'linear-gradient(120deg, rgba(8,145,178,0.08) 0%, rgba(14,165,233,0.06) 100%)' :
    GRADIENTS.brandSoft;
  const stripe =
    accent === 'ai'   ? GRADIENTS.ai   :
    accent === 'warm' ? GRADIENTS.warm :
    accent === 'teal' ? GRADIENTS.teal :
    GRADIENTS.brand;
  return (
    <div
      className="crm-anim-fadeUp"
      style={{
        position: 'relative',
        background: gradient,
        borderRadius: 14,
        padding: '18px 22px',
        marginBottom: 18,
        border: `1px solid rgba(18,38,170,0.08)`,
        overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        background: stripe,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          {icon && (
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: COLORS.white, border: `1px solid ${COLORS.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: SHADOWS.sm, flexShrink: 0,
            }}>
              {icon}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            {breadcrumb && (
              <div style={{ fontSize: 12, color: COLORS.mid, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                {breadcrumb}
              </div>
            )}
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: COLORS.dark, lineHeight: 1.2 }}>{title}</h2>
            {subtitle && <div style={{ fontSize: 13, color: COLORS.mid, marginTop: 4 }}>{subtitle}</div>}
          </div>
        </div>
        {actions && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Empty State (icon + title + description + optional CTA) ────────────────

export const EmptyState: React.FC<{
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  compact?: boolean;
}> = ({ icon, title, description, action, compact }) => (
  <div style={{
    background: COLORS.white, borderRadius: 12, border: `1px dashed ${COLORS.border}`,
    padding: compact ? '28px 20px' : '48px 24px', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
  }}>
    {icon && (
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: COLORS.blueSoft, color: COLORS.blue,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 4,
      }}>
        {icon}
      </div>
    )}
    <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.dark }}>{title}</div>
    {description && (
      <div style={{ fontSize: 13, color: COLORS.mid, maxWidth: 380, lineHeight: 1.5 }}>{description}</div>
    )}
    {action && <div style={{ marginTop: 6 }}>{action}</div>}
  </div>
);

// ─── Skeleton row (shimmer placeholder while loading) ───────────────────────

export const SkeletonRow: React.FC<{ widths?: number[]; height?: number }> = ({
  widths = [40, 18, 14, 18, 10], height = 14,
}) => (
  <div style={{
    display: 'flex', gap: 18, padding: '12px 14px',
    borderBottom: `1px solid ${COLORS.border}`, alignItems: 'center',
  }}>
    {widths.map((w, i) => (
      <div
        key={i}
        className="crm-skeleton"
        style={{ height, flex: `0 0 ${w}%` }}
      />
    ))}
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number; widths?: number[] }> = ({ rows = 5, widths }) => (
  <div style={{ background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
    {Array.from({ length: rows }).map((_, i) => <SkeletonRow key={i} widths={widths} />)}
  </div>
);

// ─── Icon tile (large file-type icon with tinted background) ────────────────

export const IconTile: React.FC<{
  ext: string;
  size?: number;
}> = ({ ext, size = 38 }) => {
  const e = (ext || '').toLowerCase();
  const palette: { bg: string; color: string } =
    e === 'pdf' ? { bg: COLORS.redSoft, color: COLORS.red } :
    e === 'xlsx' || e === 'xls' || e === 'csv' ? { bg: COLORS.greenSoft, color: COLORS.green } :
    e === 'docx' || e === 'doc' ? { bg: COLORS.blueSoft, color: COLORS.blue } :
    e === 'png' || e === 'jpg' || e === 'jpeg' || e === 'gif' || e === 'img' ? { bg: COLORS.orangeSoft, color: COLORS.orange } :
    e === 'pptx' || e === 'ppt' ? { bg: COLORS.orangeSoft, color: COLORS.orange } :
    e === 'eml' || e === 'msg' ? { bg: COLORS.purpleSoft, color: COLORS.purple } :
    { bg: COLORS.light, color: COLORS.mid };
  return (
    <div style={{
      width: size, height: size, borderRadius: 10,
      background: palette.bg, color: palette.color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, position: 'relative', fontSize: Math.round(size * 0.26),
      fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
    }}>
      <svg
        width={Math.round(size * 0.55)}
        height={Math.round(size * 0.55)}
        viewBox="0 0 24 24" fill="none" stroke={palette.color} strokeWidth="1.8"
        style={{ position: 'absolute', opacity: 0.18 }}
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span style={{ position: 'relative', fontSize: Math.round(size * 0.24) }}>{(e || '·').slice(0, 4)}</span>
    </div>
  );
};

// ─── Stat chip (compact "label: value" chip for headers) ────────────────────

export const StatChip: React.FC<{
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  accent?: 'blue' | 'green' | 'amber' | 'purple' | 'mid';
}> = ({ icon, label, value, accent = 'mid' }) => {
  const accents: Record<string, { bg: string; color: string }> = {
    blue:   { bg: COLORS.blueSoft,   color: COLORS.blue },
    green:  { bg: COLORS.greenSoft,  color: COLORS.green },
    amber:  { bg: COLORS.orangeSoft, color: COLORS.orange },
    purple: { bg: COLORS.purpleSoft, color: COLORS.purple },
    mid:    { bg: COLORS.light,      color: COLORS.mid },
  };
  const a = accents[accent];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: a.bg, color: a.color, fontSize: 12, fontWeight: 600,
    }}>
      {icon && <span style={{ display: 'inline-flex' }}>{icon}</span>}
      <span style={{ opacity: 0.75, fontWeight: 500 }}>{label}</span>
      <span>{value}</span>
    </span>
  );
};

// ─── Sparkle Badge (AI accent — small animated star pair) ───────────────────

export const SparkleBadge: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = COLORS.purple }) => (
  <span className="crm-anim-sparkle" style={{ color, lineHeight: 0 }}>
    {Icons.sparkle(color, size)}
  </span>
);

// ─── Radio group ────────────────────────────────────────────────────────────

export function RadioGroup<V extends string>(props: {
  value: V;
  onChange: (v: V) => void;
  options: Array<{ label: string; value: V; description?: string }>;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {props.options.map((o) => {
        const active = o.value === props.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => props.onChange(o.value)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left',
              padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${active ? COLORS.blue : COLORS.border}`,
              background: active ? COLORS.blueSoft : COLORS.white,
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.12s',
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: '50%',
              border: `2px solid ${active ? COLORS.blue : COLORS.border}`,
              background: active ? COLORS.blue : COLORS.white,
              flexShrink: 0, marginTop: 2,
              boxShadow: active ? `inset 0 0 0 3px ${COLORS.white}` : 'none',
            }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: active ? COLORS.blue : COLORS.dark }}>
                {o.label}
              </div>
              {o.description && (
                <div style={{ fontSize: 12, color: COLORS.mid, marginTop: 2 }}>
                  {o.description}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
